/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * SupportChatContext — the one piece of shared state between the technical
 * support form and Deskshare Support.
 *
 * Why a context at all. The widget is rendered by the application shell as
 * a sibling of the router outlet; the support form is rendered inside that
 * outlet. They have no common ancestor holding state and no way to reach each
 * other, so the form cannot tell the widget which product the visitor
 * chose and the form's nudge button cannot open the chat.
 *
 * It carries three things and nothing else:
 *
 *   selectedProduct  the canonical product name the form currently holds, or
 *                    "" for none. Written only by the form (see
 *                    SupportChatProductSync in TechnicalForm.tsx), read only
 *                    by the widget.
 *   openChat         opens the widget panel. A no-op when the widget is not
 *                    on the page.
 *   chatAvailable    whether the widget has registered itself. The nudge is
 *                    gated on this, so if the widget crashes and its error
 *                    boundary removes it, the nudge disappears rather than
 *                    offering a button that does nothing.
 *
 * Two design points that look like problems and are not:
 *
 *  1. The open handler lives in a ref, not in state. Storing a function in
 *     state would re-render every consumer of this context whenever the widget
 *     re-registered it — which happens whenever the selected product changes.
 *     The ref holds the function; a separate boolean says whether one exists.
 *
 *  2. Registering does not loop. registerOpenChat calls
 *     setChatAvailable(fn !== null); when the widget re-registers, that sets
 *     the value it already holds and React stops there. The
 *     unregister-then-register pair that runs when the handler identity
 *     changes happens inside one commit, so the two updates are batched and
 *     the value never visibly flickers to false.
 */

import { createContext, useContext } from "react";

export interface SupportChatContextValue {
  /** The canonical product name the support form currently holds, or "". */
  selectedProduct: string;
  /** Mirrors the form's product field into this context. Form-owned. */
  setSelectedProduct: (product: string) => void;
  /** Opens the widget panel. No-op when the widget is not mounted. */
  openChat: () => void;
  /** True once the widget has registered an open handler. */
  chatAvailable: boolean;
  /** Widget-owned. Pass null on unmount so the nudge disappears with it. */
  registerOpenChat: (fn: (() => void) | null) => void;
}

const noop = (): void => {};

/**
 * The default value is a working no-op rather than undefined, so a component
 * rendered outside the provider — in a focused unit test, for example — gets
 * "no product, no chat" instead of a thrown error. That is the correct
 * behaviour for every consumer here: the nudge simply does not render, and the
 * widget still works as a standalone launcher.
 */
export const SupportChatContext = createContext<SupportChatContextValue>({
  selectedProduct: "",
  setSelectedProduct: noop,
  openChat: noop,
  chatAvailable: false,
  registerOpenChat: noop,
});

/** Read the widget context. */
export function useSupportChat(): SupportChatContextValue {
  return useContext(SupportChatContext);
}
